import { getMinimapPathDistance, type SuntraceMinimapMarkerFrame } from "./minimap";

export type HudFrame = SuntraceMinimapMarkerFrame & {
  lap: number;
  totalLaps: number;
  position: number;
  racers: number;
  speedKph: number;
  gear: number;
  driftCharge: number;
  boostActive: boolean;
  raceTime: number;
  lapTime: number;
  bestLapTime: number | null;
  countdown: string;
  message: string;
  racePhase: "menu" | "countdown" | "racing" | "finished";
};

export type MenuActions = {
  start: () => void;
  resume: () => void;
  restart: () => void;
  returnToStart: () => void;
};

const SUNTRACE_VERSION = "0.1.0";

const formatTime = (seconds: number | null): string => {
  if (seconds === null || !Number.isFinite(seconds)) return "--:--.---";
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  return `${String(minutes).padStart(2, "0")}:${remainder.toFixed(3).padStart(6, "0")}`;
};

export class GameUI {
  readonly worldHost: HTMLElement;
  readonly touchRoot: HTMLElement;
  readonly #root: HTMLElement;
  readonly #menu: HTMLElement;
  readonly #hud: HTMLElement;
  readonly #pauseMenu: HTMLElement;
  readonly #finishMenu: HTMLElement;
  readonly #bootStatus: HTMLElement;
  readonly #lap: HTMLElement;
  readonly #position: HTMLElement;
  readonly #speed: HTMLElement;
  readonly #gear: HTMLElement;
  readonly #charge: HTMLElement;
  readonly #chargeFill: HTMLElement;
  readonly #pauseLap: HTMLElement;
  readonly #pausePosition: HTMLElement;
  readonly #pauseSpeed: HTMLElement;
  readonly #pauseCharge: HTMLElement;
  readonly #lapTime: HTMLElement;
  readonly #bestLap: HTMLElement;
  readonly #bestLapGroup: HTMLElement;
  readonly #countdown: HTMLElement;
  readonly #message: HTMLElement;
  readonly #minimapRoute: SVGPathElement;
  readonly #minimapRouteLength: number;
  readonly #minimapPlayer: SVGCircleElement;
  readonly #minimapRivals = new Map<string, SVGCircleElement>();
  readonly #finishRank: HTMLElement;
  readonly #finishResult: HTMLElement;
  readonly #finishTime: HTMLElement;
  readonly #finishBest: HTMLElement;
  readonly #abortController = new AbortController();

  constructor(root: HTMLElement, actions: MenuActions, totalLaps = 3) {
    this.#root = root;
    root.innerHTML = `
      <main class="game-shell">
        <div class="world" data-world aria-label="SUNTRACE three-dimensional race view"></div>
        <div class="grain" aria-hidden="true"></div>
        <section class="boot-screen" data-boot aria-live="polite">
          <div class="brand-lockup">
            <span class="brand-kicker">Meridian Works presents</span>
            <strong class="brand-mark">SUNTRACE</strong>
            <span class="brand-subtitle">Circuit Zero</span>
          </div>
          <div class="boot-rule"><span></span></div>
          <p data-boot-status>Loading Aurelia Pass</p>
        </section>
        <section class="start-screen" data-menu hidden>
          <div class="start-copy">
            <span class="menu-eyebrow">Meridian Club Series · Event 01</span>
            <div class="event-lockup">
              <h1>Aurelia<br><em>01</em></h1>
              <svg class="track-mark" viewBox="0 0 220 128" role="img" aria-label="Aurelia Pass circuit map">
                <path class="track-mark-shadow" d="M29 91C44 109 78 111 100 96C119 83 115 61 137 52C160 43 193 55 197 34C201 12 167 11 149 22C127 36 117 45 91 43C65 42 42 53 29 68C22 76 22 83 29 91Z" />
                <path class="track-mark-route" d="M29 91C44 109 78 111 100 96C119 83 115 61 137 52C160 43 193 55 197 34C201 12 167 11 149 22C127 36 117 45 91 43C65 42 42 53 29 68C22 76 22 83 29 91Z" />
                <path class="track-mark-center" d="M29 91C44 109 78 111 100 96C119 83 115 61 137 52C160 43 193 55 197 34C201 12 167 11 149 22C127 36 117 45 91 43C65 42 42 53 29 68C22 76 22 83 29 91Z" />
                <path class="track-mark-start" d="M54 104L48 91" />
                <path class="track-mark-finish" d="M43 98L48 91" />
                <circle class="track-mark-sector" cx="98" cy="97" r="3" />
                <circle class="track-mark-sector" cx="137" cy="52" r="3" />
                <circle class="track-mark-sector" cx="181" cy="45" r="3" />
                <circle class="track-mark-sector" cx="91" cy="43" r="3" />
                <path class="track-mark-elevation" d="M20 120L54 116L78 119L105 113L133 116L163 110L198 114" />
                <text class="track-mark-label" x="55" y="119">S1</text>
                <text class="track-mark-label" x="132" y="113">S2</text>
                <text class="track-mark-label" x="193" y="119">S3</text>
              </svg>
            </div>
            <div class="event-meta">
              <span><small>Local</small><strong>07:10</strong></span>
              <span><small>Surface</small><strong>Damp</strong></span>
              <span><small>Class</small><strong>GT-X</strong></span>
              <span><small>Distance</small><strong>${String(totalLaps)} laps</strong></span>
            </div>
            <p>${totalLaps === 1 ? "One lap" : `${String(totalLaps)} laps`}. Six cars. Build drift charge through clean slides, then release boost on the coast road.</p>
            <button class="primary-action" data-start type="button"><span>Start sprint</span><kbd>Enter</kbd></button>
            <div class="control-ledger" aria-label="Controls">
              <span><kbd>WASD</kbd> Drive</span>
              <span><kbd>Space</kbd> Drift</span>
              <span><kbd>Shift</kbd> Boost</span>
              <span><kbd>C</kbd> Camera</span>
            </div>
            <div class="legal-strip">
              <span>© 2026 Shuang Zheng. All rights reserved.</span>
              <span class="version-mark" data-version>VERSION ${SUNTRACE_VERSION}</span>
              <nav aria-label="Legal and credits">
                <a href="#credits">Credits</a>
                <a href="#legal">Legal</a>
              </nav>
            </div>
          </div>
        </section>
        <section class="hud" data-hud hidden>
          <div class="race-order" aria-label="Race status">
            <div class="race-order-heading">
              <span class="hud-label">Aurelia 01</span>
              <span data-lap>Lap 1 / ${String(totalLaps)}</span>
            </div>
            <div class="race-order-body">
              <strong data-position>1<small>/6</small></strong>
              <div class="timing">
                <span><small>Lap time</small><strong data-lap-time>00:00.000</strong></span>
                <span data-best-lap-group hidden><small>Best</small><strong data-best-lap>--:--.---</strong></span>
              </div>
            </div>
          </div>
          <svg class="live-minimap" data-minimap data-orientation="north-up" viewBox="0 0 220 128" role="img" aria-label="Live race minimap">
            <path class="minimap-route-shadow" d="M29 91C44 109 78 111 100 96C119 83 115 61 137 52C160 43 193 55 197 34C201 12 167 11 149 22C127 36 117 45 91 43C65 42 42 53 29 68C22 76 22 83 29 91Z" />
            <path class="minimap-route" data-minimap-route d="M29 91C44 109 78 111 100 96C119 83 115 61 137 52C160 43 193 55 197 34C201 12 167 11 149 22C127 36 117 45 91 43C65 42 42 53 29 68C22 76 22 83 29 91Z" />
            <path class="minimap-finish" d="M43 98L48 91" />
            <circle class="minimap-rival" data-minimap-rival data-rival-id="rival-1" r="3.4" hidden />
            <circle class="minimap-rival" data-minimap-rival data-rival-id="rival-2" r="3.4" hidden />
            <circle class="minimap-rival" data-minimap-rival data-rival-id="rival-3" r="3.4" hidden />
            <circle class="minimap-rival" data-minimap-rival data-rival-id="rival-4" r="3.4" hidden />
            <circle class="minimap-rival" data-minimap-rival data-rival-id="rival-5" r="3.4" hidden />
            <circle class="minimap-player" data-minimap-player r="4.8" />
          </svg>
          <div class="telemetry" aria-label="Vehicle telemetry">
            <div class="charge">
              <div class="charge-heading"><span>Drift charge</span><strong data-charge>0%</strong></div>
              <div class="charge-track"><span data-charge-fill></span></div>
            </div>
            <div class="speed-block">
              <strong data-speed>0</strong><span>km/h</span><b data-gear>N</b>
            </div>
          </div>
          <div class="race-countdown" data-countdown role="status" aria-live="polite" aria-atomic="true"></div>
          <div class="race-message" data-message role="status" aria-live="polite" aria-atomic="true"></div>
        </section>
        <section class="pause-screen" data-pause hidden aria-labelledby="pause-title">
          <div class="pause-card">
            <span class="menu-eyebrow">Meridian Club Series // Aurelia 01</span>
            <h2 id="pause-title">Race<br>paused.</h2>
            <span class="pause-version" data-version>VERSION ${SUNTRACE_VERSION}</span>
            <div class="pause-meta" aria-label="Paused race information">
              <span><small>Lap</small><strong data-pause-lap>1 / ${String(totalLaps)}</strong></span>
              <span><small>Position</small><strong data-pause-position>P1 / 6</strong></span>
              <span><small>Speed</small><strong data-pause-speed>0 km/h</strong></span>
              <span><small>Charge</small><strong data-pause-charge>0%</strong></span>
            </div>
            <div class="pause-controls" aria-label="Controls">
              <span><kbd>WASD</kbd> Drive</span>
              <span><kbd>Space</kbd> Drift</span>
              <span><kbd>Shift</kbd> Boost</span>
              <span><kbd>C</kbd> Camera</span>
            </div>
            <div class="pause-actions">
              <button class="primary-action" data-resume type="button"><span>Resume sprint</span></button>
              <button class="text-action" data-restart type="button">Restart sprint</button>
              <button class="text-action pause-link" data-back-to-start type="button">Back to start // Select event</button>
              <nav class="pause-links" aria-label="Pause menu information">
                <a href="#credits">Credits</a>
                <a href="#legal">Legal</a>
              </nav>
            </div>
            <small>CONTROLS SUSPENDED // RACE SIMULATION PAUSED</small>
          </div>
        </section>
        <section class="archive-screen" id="credits" role="dialog" aria-modal="true" aria-labelledby="credits-title" tabindex="-1">
          <article class="archive-card">
            <p class="archive-kicker">PRODUCTION RECORD // 2026</p>
            <h2 id="credits-title">SUNTRACE CREDITS</h2>
            <div class="archive-grid">
              <div>
                <h3>Original production</h3>
                <p><strong>Shuang Zheng</strong></p>
                <p>Game design, engineering, vehicle systems, environment direction, interface design, writing, sound direction, and original project production.</p>
              </div>
              <div>
                <h3>Production craft</h3>
                <p>Race direction, authored coastal world building, scene composition, camera language, performance tuning, and responsive control design.</p>
              </div>
              <div>
                <h3>Media provenance</h3>
                <p>All shipped SUNTRACE models, textures, environment modules, interface work, and writing are original project work. The game uses system font fallbacks and browser-delivered rendering technology.</p>
              </div>
              <div>
                <h3>Rights</h3>
                <p>© 2026 Shuang Zheng. All rights reserved.</p>
                <p>Third-party software remains subject to its respective license.</p>
              </div>
            </div>
            <nav class="archive-actions" aria-label="Credits navigation">
              <a href="#legal">Legal</a>
              <a href="#">Close</a>
            </nav>
          </article>
        </section>
        <section class="archive-screen" id="legal" role="dialog" aria-modal="true" aria-labelledby="legal-title" tabindex="-1">
          <article class="archive-card">
            <p class="archive-kicker">RELEASE PROTECTION // LEGAL</p>
            <h2 id="legal-title">SUNTRACE LEGAL</h2>
            <h3>Copyright notice</h3>
            <p>© 2026 Shuang Zheng. All rights reserved.</p>
            <h3>Original materials</h3>
            <p>The proprietary rights notice covers original SUNTRACE code, artwork, models, textures, audio direction, writing, interface work, and other original materials owned by Shuang Zheng. No ownership is claimed over third-party materials.</p>
            <h3>Third-party materials</h3>
            <p>Three.js, meshoptimizer, and other dependencies retain their own copyrights and licenses. Their notices remain available in their respective packages.</p>
            <h3>Browser delivery</h3>
            <p>Browser-delivered materials can be inspected or extracted by determined users. This notice does not claim technical protection beyond the rights described above.</p>
            <nav class="archive-actions" aria-label="Legal navigation">
              <a href="#credits">Credits</a>
              <a href="#">Close</a>
            </nav>
          </article>
        </section>
        <section class="finish-screen" data-finish hidden aria-live="polite">
          <div class="finish-card">
            <div class="finish-topline">
              <span class="finish-stamp">FINISH</span>
              <span class="finish-event">RACE COMPLETE · Aurelia Pass · Event 01</span>
            </div>
            <div class="finish-heading">
              <div class="finish-rank" aria-label="Final race rank">
                <small>Rank</small>
                <strong data-finish-rank>P1<small>/6</small></strong>
              </div>
              <div>
                <span class="finish-classified">CLASSIFIED</span>
                <h2 data-finish-result>First light,<br>first place.</h2>
              </div>
            </div>
            <div class="finish-metrics" aria-label="Race result times">
              <span><small>Final time</small><strong data-finish-time>--:--.---</strong></span>
              <span><small>Best lap</small><strong data-finish-best>--:--.---</strong></span>
            </div>
            <button class="primary-action" data-finish-restart type="button"><span>Race again</span><kbd>Enter</kbd></button>
          </div>
        </section>
        <div class="touch-controls" data-touch-controls aria-label="Touch driving controls">
          <div class="touch-system">
            <button data-input-event="pause" type="button" aria-label="Pause"><strong aria-hidden="true">II</strong><small>Pause</small></button>
            <button data-input-event="recover" type="button" aria-label="Recover"><strong aria-hidden="true">↺</strong><small>Reset</small></button>
          </div>
          <div class="touch-steer">
            <button data-drive-action="left" type="button" aria-label="Steer left"><strong aria-hidden="true">←</strong><small>Left</small></button>
            <button data-drive-action="right" type="button" aria-label="Steer right"><strong aria-hidden="true">→</strong><small>Right</small></button>
          </div>
          <div class="touch-pedals">
            <button data-drive-action="brake" type="button" aria-label="Brake"><strong aria-hidden="true">B</strong><small>Brake</small></button>
            <button data-drive-action="drift" type="button" aria-label="Drift"><strong aria-hidden="true">D</strong><small>Drift</small></button>
            <button data-drive-action="boost" type="button" aria-label="Boost"><strong aria-hidden="true">+</strong><small>Boost</small></button>
            <button data-drive-action="throttle" type="button" aria-label="Go"><strong aria-hidden="true">GO</strong><small>Throttle</small></button>
          </div>
        </div>
      </main>
    `;

    this.worldHost = this.#require("[data-world]");
    this.touchRoot = this.#require("[data-touch-controls]");
    this.#menu = this.#require("[data-menu]");
    this.#hud = this.#require("[data-hud]");
    this.#pauseMenu = this.#require("[data-pause]");
    this.#finishMenu = this.#require("[data-finish]");
    this.#bootStatus = this.#require("[data-boot-status]");
    this.#lap = this.#require("[data-lap]");
    this.#position = this.#require("[data-position]");
    this.#speed = this.#require("[data-speed]");
    this.#gear = this.#require("[data-gear]");
    this.#charge = this.#require("[data-charge]");
    this.#chargeFill = this.#require("[data-charge-fill]");
    this.#pauseLap = this.#require("[data-pause-lap]");
    this.#pausePosition = this.#require("[data-pause-position]");
    this.#pauseSpeed = this.#require("[data-pause-speed]");
    this.#pauseCharge = this.#require("[data-pause-charge]");
    this.#lapTime = this.#require("[data-lap-time]");
    this.#bestLap = this.#require("[data-best-lap]");
    this.#bestLapGroup = this.#require("[data-best-lap-group]");
    this.#countdown = this.#require("[data-countdown]");
    this.#message = this.#require("[data-message]");
    this.#minimapRoute = this.#requireSvgPath("[data-minimap-route]");
    this.#minimapRouteLength = this.#minimapRoute.getTotalLength();
    this.#minimapPlayer = this.#requireSvgCircle("[data-minimap-player]");
    for (const marker of this.#root.querySelectorAll<SVGCircleElement>("[data-minimap-rival]")) {
      const rivalId = marker.dataset.rivalId;
      if (rivalId !== undefined) this.#minimapRivals.set(rivalId, marker);
    }
    this.#finishRank = this.#require("[data-finish-rank]");
    this.#finishResult = this.#require("[data-finish-result]");
    this.#finishTime = this.#require("[data-finish-time]");
    this.#finishBest = this.#require("[data-finish-best]");
    const options = { signal: this.#abortController.signal };
    this.#require("[data-start]").addEventListener("click", actions.start, options);
    this.#require("[data-resume]").addEventListener("click", actions.resume, options);
    this.#require("[data-restart]").addEventListener("click", actions.restart, options);
    this.#require("[data-back-to-start]").addEventListener("click", actions.returnToStart, options);
    this.#require("[data-finish-restart]").addEventListener("click", actions.restart, options);
    window.addEventListener("keydown", (event) => {
      if (event.code !== "Enter") return;
      if (!this.#menu.hidden) actions.start();
      else if (!this.#pauseMenu.hidden) actions.resume();
      else if (!this.#finishMenu.hidden) actions.restart();
    }, options);
  }

  setBootProgress(label: string, progress: number): void {
    this.#bootStatus.textContent = label;
    this.#root.style.setProperty("--boot-progress", `${String(Math.round(progress * 100))}%`);
  }

  showMenu(): void {
    this.#require("[data-boot]").setAttribute("hidden", "");
    this.#menu.removeAttribute("hidden");
    this.#hud.setAttribute("hidden", "");
    this.#pauseMenu.setAttribute("hidden", "");
    this.#finishMenu.setAttribute("hidden", "");
    this.#root.dataset.state = "menu";
    delete this.#root.dataset.paused;
  }

  startRace(): void {
    this.#menu.setAttribute("hidden", "");
    this.#pauseMenu.setAttribute("hidden", "");
    this.#finishMenu.setAttribute("hidden", "");
    this.#hud.removeAttribute("hidden");
    this.#root.dataset.state = "racing";
  }

  setPaused(paused: boolean): void {
    this.#pauseMenu.toggleAttribute("hidden", !paused);
    this.#root.dataset.paused = String(paused);
  }

  update(frame: HudFrame): void {
    this.#lap.textContent = `Lap ${String(Math.min(frame.lap, frame.totalLaps))} / ${String(frame.totalLaps)}`;
    this.#position.innerHTML = `${String(frame.position)}<small>/${String(frame.racers)}</small>`;
    this.#speed.textContent = String(Math.round(frame.speedKph));
    this.#gear.textContent = frame.gear === 0 ? "N" : `G${String(frame.gear)}`;
    this.#charge.textContent = frame.boostActive ? "BOOST" : `${String(Math.round(frame.driftCharge * 100))}%`;
    this.#pauseLap.textContent = `Lap ${String(Math.min(frame.lap, frame.totalLaps))} / ${String(frame.totalLaps)}`;
    this.#pausePosition.textContent = `P${String(frame.position)} / ${String(frame.racers)}`;
    this.#pauseSpeed.textContent = `${String(Math.round(frame.speedKph))} km/h`;
    this.#pauseCharge.textContent = frame.boostActive ? "BOOST" : `${String(Math.round(frame.driftCharge * 100))}%`;
    this.#chargeFill.style.setProperty("--charge", `${String(Math.max(0, Math.min(1, frame.driftCharge)) * 100)}%`);
    this.#charge.closest(".charge")?.toggleAttribute("data-boosting", frame.boostActive);
    this.#lapTime.textContent = formatTime(frame.lapTime);
    this.#bestLap.textContent = formatTime(frame.bestLapTime);
    this.#bestLapGroup.toggleAttribute("hidden", frame.bestLapTime === null);
    this.#countdown.textContent = frame.countdown;
    this.#countdown.toggleAttribute("data-visible", frame.countdown.length > 0);
    this.#message.textContent = frame.message;
    this.#message.toggleAttribute("data-visible", frame.message.length > 0);
    this.#updateMinimapMarker(this.#minimapPlayer, frame.playerTrackProgress);
    for (const marker of this.#minimapRivals.values()) marker.setAttribute("hidden", "");
    for (const rival of frame.rivals) {
      const marker = this.#minimapRivals.get(rival.id);
      if (marker === undefined) continue;
      marker.toggleAttribute("hidden", !rival.visible);
      this.#updateMinimapMarker(marker, rival.trackProgress);
    }
    if (frame.racePhase === "finished") {
      this.#finishRank.innerHTML = `P${String(frame.position)}<small>/${String(frame.racers)}</small>`;
      const ordinal = frame.position === 1 ? "First light,<br>first place." : `Dawn result:<br>P${String(frame.position)}.`;
      this.#finishResult.innerHTML = ordinal;
      this.#finishTime.textContent = formatTime(frame.raceTime);
      this.#finishBest.textContent = formatTime(frame.bestLapTime);
      this.#hud.setAttribute("hidden", "");
      this.#finishMenu.removeAttribute("hidden");
      this.#root.dataset.state = "finished";
    }
  }

  showError(message: string): void {
    this.#bootStatus.textContent = message;
    this.#root.dataset.error = "true";
  }

  dispose(): void {
    this.#abortController.abort();
  }

  #require(selector: string): HTMLElement {
    const element = this.#root.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing UI element: ${selector}`);
    return element;
  }

  #requireSvgPath(selector: string): SVGPathElement {
    const element = this.#root.querySelector<SVGPathElement>(selector);
    if (!element) throw new Error(`Missing UI element: ${selector}`);
    return element;
  }

  #requireSvgCircle(selector: string): SVGCircleElement {
    const element = this.#root.querySelector<SVGCircleElement>(selector);
    if (!element) throw new Error(`Missing UI element: ${selector}`);
    return element;
  }

  #updateMinimapMarker(marker: SVGCircleElement, progress: number): void {
    const point = this.#minimapRoute.getPointAtLength(
      getMinimapPathDistance(progress, this.#minimapRouteLength),
    );
    marker.setAttribute("cx", String(point.x));
    marker.setAttribute("cy", String(point.y));
  }
}
